import React, { useEffect, useMemo, useState } from 'react';
import { FmlxCheckBox } from 'fmlx-common-ui';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { InputWithIcon } from 'components/input';
import NoSearchResults from 'components/generic/noSearchResults';
import { useRMDispatch } from 'redux/hooks';
import { useSelectArchivedTasks } from 'redux/hooks/unarchiving';
import { fetchArchivedTasksThunk } from 'redux/thunks/unarchiving';
import { ArchivedTasksListProps } from './constants';

// Orange manila folder, matching the Explorer tree's project-folder icon
// (RMSvgIcon "Folder" with treeIconColors.Folder).
const FOLDER_ICON_COLOR = '#CE873A';

export const ArchivedTasksList = (props: ArchivedTasksListProps) => {
  const { selectedTaskIds, setSelectedTaskIds, disabled = false } = props;
  const [searchText, setSearchText] = useState('');
  const dispatch = useRMDispatch();
  const { data: tasks } = useSelectArchivedTasks();

  useEffect(() => {
    dispatch(fetchArchivedTasksThunk());
  }, [dispatch]);

  const filteredTasks = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    if (!text) {
      return tasks;
    }
    return tasks.filter((task) => task.name?.toLowerCase().includes(text));
  }, [tasks, searchText]);

  const selectedSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);

  const toggleTask = (id: number) => {
    if (disabled) {
      return;
    }
    setSelectedTaskIds((prev) =>
      prev.includes(id) ? prev.filter((taskId) => taskId !== id) : [...prev, id],
    );
  };

  const allFilteredSelected =
    filteredTasks.length > 0 &&
    filteredTasks.every((task) => selectedSet.has(task.jobId));
  const someFilteredSelected = filteredTasks.some((task) => selectedSet.has(task.jobId));
  const isIndeterminate = someFilteredSelected && !allFilteredSelected;

  const toggleSelectAll = () => {
    if (disabled) {
      return;
    }
    const filteredIds = filteredTasks.map((task) => task.jobId);
    setSelectedTaskIds((prev) => {
      if (allFilteredSelected) {
        return prev.filter((id) => !filteredIds.includes(id));
      }
      return Array.from(new Set([...prev, ...filteredIds]));
    });
  };

  return (
    <>
      <div className="shrink-0">
        <InputWithIcon
          key={'archivedTasksSearch'}
          icon={<RMSvgIcon name="Search" height="1.25rem" width="1.25rem" />}
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
          placeholder="Search archived file"
          inputStyle={{ height: '2.5rem' }}
          iconPosition="left"
        />
      </div>
      <div
        className={`flex-1 min-h-0 flex flex-col border-solid border border-[#E0E0E0] rounded-sm overflow-hidden ${
          disabled ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        {filteredTasks.length > 0 && (
          <div
            onClick={toggleSelectAll}
            className="shrink-0 flex cursor-pointer items-center gap-2 px-3 py-2 border-0 border-b border-solid border-[#E0E0E0] hover:bg-[#F5F5F5]"
          >
            <span className="w-5 shrink-0" />
            <span className="text-sm font-medium text-gray-900">Select All</span>
            <span className="ml-auto">
              <FmlxCheckBox
                checked={allFilteredSelected}
                indeterminate={isIndeterminate}
                disabled={disabled}
              />
            </span>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto">
          {filteredTasks.length === 0 ? (
            searchText.trim() ? (
              <NoSearchResults testId="archived-tasks-no-search-results" />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-[#737373]">
                No archived tasks found
              </div>
            )
          ) : (
            filteredTasks.map((task) => (
              <div
                key={task.jobId}
                onClick={() => toggleTask(task.jobId)}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-[#F5F5F5] ${
                  selectedSet.has(task.jobId) ? 'bg-[#EEF4FF]' : ''
                }`}
              >
                <RMSvgIcon
                  name="Folder"
                  fill={FOLDER_ICON_COLOR}
                  height="1.25rem"
                  width="1.25rem"
                  className="shrink-0"
                />
                <span
                  className="min-w-0 flex-1 truncate text-sm text-gray-900"
                  title={task.name}
                >
                  {task.name}
                </span>
                <span className="ml-auto shrink-0">
                  <FmlxCheckBox
                    checked={selectedSet.has(task.jobId)}
                    disabled={disabled}
                  />
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};
